import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, { clearStoredAuthToken } from '../../services/api';
import { normalizeAuthUser } from '../../utils/profileUtils';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [dashboardMeta, setDashboardMeta] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const setUser = useCallback((nextUser) => {
    setUserState(normalizeAuthUser(nextUser));
  }, []);

  const applyDashboardResponse = useCallback((data) => {
    if (data?.user) {
      setUserState(normalizeAuthUser(data.user));
    }
    if (data?.dashboard !== undefined) {
      setDashboardMeta(data.dashboard);
    }
    return data;
  }, []);

  const refreshDashboard = useCallback(async () => {
    const { data } = await api.get('/dashboard');
    return applyDashboardResponse(data);
  }, [applyDashboardResponse]);

  const logout = () => {
    clearStoredAuthToken();
    delete api.defaults.headers.common['Authorization'];
    setUserState(null);
    setDashboardMeta(null);
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await refreshDashboard();
      } catch (e) {
        if (e.response?.status === 401) {
          clearStoredAuthToken();
          delete api.defaults.headers.common['Authorization'];
          setUserState(null);
          setDashboardMeta(null);
        }
      } finally {
        setAuthLoading(false);
      }
    };

    bootstrap();
  }, [refreshDashboard]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        dashboardMeta,
        authLoading,
        refreshDashboard,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext, AuthProvider, useAuth };
